import {
  forwardRef,
  useState,
  createContext,
  useContext,
  type CSSProperties,
  type ElementRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type RefAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Provider, Root, Trigger, Content } from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

const TooltipProvider = Provider;

const TooltipRoot = Root;

const TooltipContext = createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);

const Tooltip = ({
  children,
  delayDuration = 0,
  ...props
}: ComponentPropsWithoutRef<typeof Root>) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = props.open !== undefined;
  const open = isControlled ? props.open ?? false : internalOpen;
  const setOpen = isControlled
    ? (newOpen: boolean) => props.onOpenChange?.(newOpen)
    : setInternalOpen;

  return (
    <TooltipContext.Provider value={{ open, setOpen }}>
      <TooltipRoot
        {...props}
        delayDuration={delayDuration}
        open={open}
        onOpenChange={setOpen}
      >
        {children}
      </TooltipRoot>
    </TooltipContext.Provider>
  );
};

const TooltipTrigger = forwardRef<
  ElementRef<typeof Trigger>,
  ComponentPropsWithoutRef<typeof Trigger> & RefAttributes<HTMLButtonElement>
>(({ children, className, asChild = true, onClick, ...props }, ref) => {
  const context = useContext(TooltipContext);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (context) {
      context.setOpen(!context.open);
    }

    onClick?.(e);
  };

  return (
    <Trigger
      {...props}
      type="button"
      ref={ref}
      asChild={asChild}
      onClick={handleClick}
      onPointerDown={(e) => e.preventDefault()}
    >
      {children}
    </Trigger>
  );
});

TooltipTrigger.displayName = Trigger.displayName;

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipContentProps extends ComponentPropsWithoutRef<typeof Content> {
  position?: TooltipPosition;
}

const TooltipContent = forwardRef<
  ElementRef<typeof Content>,
  TooltipContentProps
>(
  (
    { className, position = 'top', sideOffset = 5, children, ...props },
    ref,
  ) => (
    <Content
      ref={ref}
      side={position}
      sideOffset={sideOffset}
      avoidCollisions={true}
      collisionPadding={8}
      className={cn(
        'relative z-50 inline-block max-w-xs px-3 py-1.5',
        'rounded-md border border-border bg-muted text-foreground',
        'text-center text-sm',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    >
      {children}
      {/* <TooltipArrow position={position} /> */}
    </Content>
  ),
);
TooltipContent.displayName = Content.displayName;

const TooltipArrow = ({ position }: { position: TooltipPosition }) => {
  const arrowPositionStyles = {
    top: 'bottom-full rotate-180',
    bottom: 'top-full',
    left: 'right-full rotate-90',
    right: 'left-full -rotate-90',
  };

  return (
    <svg
      className={cn('absolute z-50', arrowPositionStyles[position])}
      width="11"
      height="5"
      viewBox="0 0 11 5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0L11 0L5.5 5L0 0Z" fill="hsl(var(--background))" />
    </svg>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };

type TooltipWrapperProps = {
  content: ReactNode;
  children: ReactNode;
  offset?: number;
  align?: 'left' | 'center';
  className?: string;
  fixed?: boolean;
  direction?: 'top' | 'left' | 'right' | 'bottom';
  disabled?: boolean;
  portal?: boolean;
};

const PORTAL_GAP_PX = 8;

const getPortalStyle = (
  rect: DOMRect,
  direction: TooltipPosition,
): CSSProperties => {
  const clampX = (x: number) =>
    Math.min(Math.max(x, PORTAL_GAP_PX), window.innerWidth - PORTAL_GAP_PX);

  switch (direction) {
    case 'top':
      return {
        left: clampX(rect.left + rect.width / 2),
        bottom: window.innerHeight - rect.top + PORTAL_GAP_PX,
        transform: 'translateX(-50%)',
      };
    case 'bottom':
      return {
        left: clampX(rect.left + rect.width / 2),
        top: rect.bottom + PORTAL_GAP_PX,
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        right: window.innerWidth - rect.left + PORTAL_GAP_PX,
        top: rect.top + rect.height / 2,
        transform: 'translateY(-50%)',
      };
    case 'right':
      return {
        left: rect.right + PORTAL_GAP_PX,
        top: rect.top + rect.height / 2,
        transform: 'translateY(-50%)',
      };
  }
};

const getStyles = ({
  direction = 'top',
  offset = 1.2,
}: Pick<TooltipWrapperProps, 'direction' | 'offset'>) => {
  switch (direction) {
    case 'top':
      return { bottom: `${offset * 100}%` };
    case 'left':
      return { right: `${offset * 100}%` };
    case 'right':
      return { left: `${offset * 100}%` };
    case 'bottom':
      return { top: `${offset * 100}%` };
  }
};

export default function TooltipWrapper({
  children,
  disabled = false,
  content,
  align = 'center',
  className,
  fixed,
  direction = 'top',
  offset = 1.2,
  portal,
}: TooltipWrapperProps) {
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  if (portal) {
    const show = (event: { currentTarget: HTMLElement }) =>
      setTriggerRect(event.currentTarget.getBoundingClientRect());

    return (
      <div
        className="relative"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={show}
        onMouseLeave={() => setTriggerRect(null)}
        onFocus={show}
        onBlur={() => setTriggerRect(null)}
      >
        {children}
        {!disabled &&
          triggerRect &&
          createPortal(
            <div
              role="tooltip"
              style={getPortalStyle(triggerRect, direction)}
              className={cn(
                'pointer-events-none fixed z-[100] w-56 max-w-[calc(100vw-1rem)] rounded-md border border-border bg-muted px-3 py-2 text-center font-sans text-sm tracking-wide text-foreground sm:w-80',
                className,
              )}
            >
              {content}
            </div>,
            document.body,
          )}
      </div>
    );
  }

  return (
    <div
      className={cn('group/tooltip', !fixed && 'relative')}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      {!disabled ? (
        fixed ? (
          <div
            style={getStyles({ direction, offset })}
            className={cn(
              'pointer-events-none absolute flex items-center justify-center opacity-0 transition-opacity group-hover/tooltip:pointer-events-auto group-hover/tooltip:opacity-100',
              className,
            )}
          >
            <div className="fixed w-80 max-w-max translate-x-3 rounded-md border border-border bg-muted px-3 py-2 text-center text-sm text-foreground">
              {content}
            </div>
          </div>
        ) : (
          <div
            style={getStyles({ direction, offset })}
            className={cn(
              'pointer-events-none absolute z-50 w-56 max-w-max rounded-md border border-border bg-muted px-3 py-2 text-center font-sans text-sm tracking-wide text-foreground opacity-0 transition-opacity group-hover/tooltip:pointer-events-auto group-hover/tooltip:opacity-100 sm:w-80',
              {
                'left-1/2 -translate-x-1/2':
                  align === 'center' &&
                  direction !== 'left' &&
                  direction !== 'right',
                'left-0': align === 'left',
                'top-1/2 -translate-y-1/2':
                  align === 'center' &&
                  (direction === 'left' || direction === 'right'),
              },
              className,
            )}
          >
            {content}
          </div>
        )
      ) : (
        <></>
      )}
    </div>
    // <TooltipProvider delayDuration={0}>
    //   <Tooltip>
    //     <TooltipTrigger asChild>{children}</TooltipTrigger>
    //     <TooltipContent>{content}</TooltipContent>
    //   </Tooltip>
    // </TooltipProvider>
  );
}
